import React, { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Trash2, AlertCircle, Save } from 'lucide-react';
import { useCompany } from '../../contexts/CompanyContext';
import { quizzes } from '../../lib/api';
import { cn } from '../../utils/cn';
import { QuestionEditor } from '../../components/QuestionEditor';
import { QuestionTemplateSelector } from '../../components/QuestionTemplateSelector';
import type { TrainingModule, Quiz, Question, QuestionOption } from '../../types';

interface QuizManagementPageProps {
  module: TrainingModule;
  onBack: () => void;
  onSave: () => void;
}

const defaultQuestion: Omit<Question, 'id'> = {
  text: '',
  type: 'single',
  options: [
    { id: crypto.randomUUID(), text: '', isCorrect: false, feedback: '' },
    { id: crypto.randomUUID(), text: '', isCorrect: false, feedback: '' },
    { id: crypto.randomUUID(), text: '', isCorrect: false, feedback: '' },
    { id: crypto.randomUUID(), text: '', isCorrect: false, feedback: '' }
  ],
  points: 1,
  required: true
};

export function QuizManagementPage({ module, onBack, onSave }: QuizManagementPageProps) {
  const { company } = useCompany();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  useEffect(() => {
    console.log('Loading quiz for module:', module.id);
    if (module.quiz) {
      console.log('Existing quiz found:', module.quiz);
      setQuestions(module.quiz.questions.map(q => ({
        ...q,
        id: q.id || crypto.randomUUID(),
        options: q.options.map(opt => ({
          ...opt,
          id: opt.id || crypto.randomUUID(),
          isCorrect: Boolean(opt.isCorrect),
          feedback: opt.feedback || ''
        }))
      })));
    }
  }, [module]);

  const validateQuestions = () => {
    console.log('Validating questions:', questions);
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      
      if (!q.text.trim()) {
        setError(`Questão ${i + 1}: Texto da questão é obrigatório`);
        return false;
      }

      if (q.options.length < 2) {
        setError(`Questão ${i + 1}: Mínimo de 2 opções por questão`);
        return false;
      }

      if (!q.options.some(opt => opt.isCorrect)) {
        setError(`Questão ${i + 1}: Selecione pelo menos uma opção correta`);
        return false;
      }

      if (q.options.some(opt => !opt.text.trim())) {
        setError(`Questão ${i + 1}: Todas as opções devem ter texto`);
        return false;
      }

      if (q.points < 1) {
        setError(`Questão ${i + 1}: Pontuação mínima é 1`);
        return false;
      }
    }

    return true;
  };

  const handleAddQuestion = () => {
    console.log('Adding new question');
    const newQuestion = {
      ...defaultQuestion,
      id: crypto.randomUUID(),
      options: defaultQuestion.options.map(opt => ({
        ...opt,
        id: crypto.randomUUID()
      }))
    };
    setQuestions(prev => [...prev, newQuestion]);
    setSelectedQuestionIndex(questions.length);
  };

  const handleQuestionChange = (field: keyof Question, value: any) => {
    console.log('Updating question field:', field, 'with value:', value);
    setQuestions(prev => {
      const updated = [...prev];
      updated[selectedQuestionIndex] = {
        ...updated[selectedQuestionIndex],
        [field]: value
      };
      return updated;
    });
  };

  const handleOptionChange = (index: number, field: keyof QuestionOption, value: any) => {
    console.log('Updating option:', index, 'field:', field, 'value:', value);
    setQuestions(prev => {
      const updated = [...prev];
      const question = updated[selectedQuestionIndex];
      const options = [...question.options];
      
      if (field === 'isCorrect' && question.type === 'single') {
        options.forEach(opt => opt.isCorrect = false);
      }
      
      options[index] = {
        ...options[index],
        [field]: value
      };
      
      updated[selectedQuestionIndex] = {
        ...question,
        options
      };
      return updated;
    });
  };

  const handleDeleteQuestion = (index: number) => {
    if (!confirm('Tem certeza que deseja excluir esta questão?')) return;
    
    console.log('Deleting question:', index);
    setQuestions(prev => prev.filter((_, i) => i !== index));
    
    if (selectedQuestionIndex >= questions.length - 1) {
      setSelectedQuestionIndex(Math.max(0, questions.length - 2));
    }
  };

  const handleAddOption = () => {
    if (questions[selectedQuestionIndex].options.length >= 6) {
      setError('Máximo de 6 opções por questão.');
      return;
    }

    console.log('Adding new option to question:', selectedQuestionIndex);
    setQuestions(prev => {
      const updated = [...prev];
      const question = updated[selectedQuestionIndex];
      question.options.push({
        id: crypto.randomUUID(),
        text: '',
        isCorrect: false,
        feedback: ''
      });
      return updated;
    });
  };

  const handleDeleteOption = (index: number) => {
    const question = questions[selectedQuestionIndex];
    if (question.options.length <= 2) {
      setError('Mínimo de 2 opções por questão.');
      return;
    }

    console.log('Deleting option:', index, 'from question:', selectedQuestionIndex);
    setQuestions(prev => {
      const updated = [...prev];
      const question = updated[selectedQuestionIndex];
      question.options = question.options.filter((_, i) => i !== index);
      return updated;
    });
  };

  const handleSave = async () => {
    try {
      console.log('Attempting to save quiz...');
      setSaving(true);
      setError(null);

      if (!validateQuestions()) {
        console.log('Validation failed');
        return;
      }

      // Save to Supabase
      if (module.quiz) {
        console.log('Updating existing quiz:', module.quiz.id);
        await quizzes.update(module.quiz.id, questions);
      } else {
        console.log('Creating new quiz for module:', module.id);
        await quizzes.create(module.id, questions);
      }

      console.log('Quiz saved successfully');
      onSave();
    } catch (err: any) {
      console.error('Error saving quiz:', err);
      setError(err.message || 'Erro ao salvar questionário. Por favor, tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const selectedQuestion = questions[selectedQuestionIndex];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className={cn(
              "p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100",
              "transition-all"
            )}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Questionário: {module.title}
            </h1>
            <p className="mt-1 text-gray-500">
              Gerencie as questões do módulo
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowTemplates(prev => !prev)}
            className={cn(
              "flex items-center gap-2 rounded-lg px-4 py-2",
              "text-gray-700 border border-gray-300",
              "transition-all hover:bg-gray-50 active:scale-95"
            )}
          >
            {showTemplates ? 'Voltar às Questões' : 'Usar Modelo'}
          </button>
          <button
            onClick={handleAddQuestion}
            className={cn(
              "flex items-center gap-2 rounded-lg px-4 py-2",
              "text-white transition-all hover:opacity-90 active:scale-95",
              "bg-[#7c3aed]"
            )}
          >
            <Plus className="h-4 w-4" />
            Nova Questão
          </button>
          <button
            onClick={handleSave}
            disabled={saving || questions.length === 0}
            className={cn(
              "flex items-center gap-2 rounded-lg px-4 py-2 text-white",
              "transition-all hover:opacity-90 active:scale-95 bg-green-600",
              "disabled:opacity-50 disabled:hover:opacity-50 disabled:active:scale-100"
            )}
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Salvar Alterações
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {showTemplates ? (
        <QuestionTemplateSelector
          onSelect={(template) => {
            setQuestions(prev => [...prev, {
              ...template,
              id: crypto.randomUUID()
            }]);
            setSelectedQuestionIndex(questions.length);
            setShowTemplates(false);
          }}
        />
      ) : (
        <>
          {/* Question Navigation */}
          <div className="flex items-center gap-2">
            {questions.map((_, index) => (
              <button
                key={index}
                onClick={() => setSelectedQuestionIndex(index)}
                className={cn(
                  "w-8 h-8 rounded-full font-medium",
                  "transition-colors",
                  selectedQuestionIndex === index
                    ? "bg-purple-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                )}
              >
                {index + 1}
              </button>
            ))}
          </div>

          {questions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">
                Nenhuma questão adicionada. Clique em "Nova Questão" para começar.
              </p>
            </div>
          ) : selectedQuestion ? (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Questão {selectedQuestionIndex + 1}
                </h3>
                <button
                  onClick={() => handleDeleteQuestion(selectedQuestionIndex)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>

              <QuestionEditor
                question={selectedQuestion}
                onChange={handleQuestionChange}
                onOptionChange={handleOptionChange}
                onDeleteOption={handleDeleteOption}
                onAddOption={handleAddOption}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}